import { ODSCardBasic } from '@telekom-ods/react-ui-kit';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import Provision from '../../../../../api/provision';
import Table from '../../../../../components/Table';
import useApiQuery from '../../../../../hooks/useApiQuery';
import ReportStyle from '../../../../Reports/Reports.module.css';

import Alert from '@/components/Alert';
import type { ErrorType } from '@/types';
import type { CaasCluster, ClusterNode } from '@/types/productTypes';
import type { TableRowData } from '@/types/tableTypes';

interface CaasClusterNodesProps {
  cluster: CaasCluster;
}

const CaasClusterNodes = ({ cluster }: CaasClusterNodesProps): React.JSX.Element | null => {
  const intl = useIntl();
  const {
    data: clusterNodes = null,
    error: fetchError,
    makeApiCall: getCaasNodes,
  } = useApiQuery<ClusterNode[]>(
    Provision.fetchCaasClusterNodes as unknown as (args: unknown) => Promise<ClusterNode[]>
  );

  useEffect(() => {
    if (cluster.name) {
      getCaasNodes(cluster.name);
    }
  }, [cluster.name, getCaasNodes]);

  const fields = [
    { title: { id: 'name' }, id: 'name' },
    { title: { id: 'hostname' }, id: 'hostname' },
    { title: { id: cluster.type === 'baremetal' ? 'profile' : 'flavor' }, id: 'size' },
    { title: { id: 'cpu' }, id: 'cpu' },
    { title: { id: 'memory' }, id: 'memory' },
    { title: { id: 'disk' }, id: 'disk' },
  ];

  const rows = clusterNodes?.map((node: ClusterNode, index: number) => {
    const size = node.infrastructure === 'baremetal' ? node.hw_version : node.vm_flavor;
    return {
      key: node.name || `node-${String(index)}`,
      data: [
        { field: 'name', value: node.name },
        { field: 'hostname', value: node.hostname },
        { field: 'size', value: size ?? '' },
        { field: 'cpu', value: node.cpu },
        { field: 'memory', value: node.memory },
        { field: 'disk', value: node.disk },
      ],
    };
  }) as TableRowData[];

  if (!clusterNodes && !fetchError) {
    return null;
  }

  return clusterNodes ? (
    <Table
      header={intl.formatMessage({ id: 'clusterNodes' })}
      fields={fields}
      rows={rows}
      error={fetchError as ErrorType}
    />
  ) : (
    <ODSCardBasic
      size='large'
      className={ReportStyle.tenant_details_card_without_spacing ?? ''}
      style={{
        width: '90%',
        margin: '0 auto',
      }}
      contentSlot={
        <div id='caas-details'>
          <Alert
            className={ReportStyle.select_tenant_alert ?? ''}
            mode='error'
            title={intl.formatMessage({ id: 'clusterNodesFetchError' })}
            text={fetchError?.message ?? 'Failed to load cluster nodes'}
          />
        </div>
      }
      tabIndex={-1}
    />
  );
};

export default CaasClusterNodes;
